import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showSuccess, showError } from '../utils/alerts';
import userService, { User, CreateUserRequest, UpdateUserRequest } from '../services/userService';
import { confirmAlert } from 'react-confirm-alert';
import { useAuth } from '../context/AuthContext';

const userValidationSchema = yup.object({
  email: yup.string().email('Email inválido').required('El email es requerido'),
  password: yup.string().min(8, 'La contraseña debe tener al menos 8 caracteres').required('La contraseña es requerida'),
  first_name: yup.string().required('El nombre es requerido').min(2, 'El nombre debe tener al menos 2 caracteres'),
  last_name: yup.string().required('El apellido es requerido').min(2, 'El apellido debe tener al menos 2 caracteres'),
  role_id: yup.number().required('El rol es requerido')
});

const passwordValidationSchema = yup.object({
  password: yup.string().min(8, 'La contraseña debe tener al menos 8 caracteres').required('La contraseña es requerida')
});

const UserManager: React.FC = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  
  // Helper function to check if current user can interact with superusers
  const canManageSuperusers = () => {
    return currentUser?.role?.name === 'superusuario';
  };
  
  // Helper function to check if a user is a superuser
  const isSuperuser = (user: User) => {
    return user.role?.name === 'superusuario';
  };
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const {
    register: registerUser,
    handleSubmit: handleSubmitUser,
    formState: { errors: userErrors, isSubmitting: isSubmittingUser },
    reset: resetUser,
  } = useForm<CreateUserRequest>({
    resolver: yupResolver(userValidationSchema)
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
    reset: resetPassword
  } = useForm<{ password: string }>({
    resolver: yupResolver(passwordValidationSchema)
  });

  // Get users
  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: userService.getAllUsers
  });

  // Get roles
  const { data: roles = [], isLoading: isLoadingRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: userService.getRoles
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showSuccess('Usuario creado exitosamente');
      setShowModal(false);
      resetUser();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error al crear usuario';
      showError(message);
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) => 
      userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showSuccess('Usuario actualizado exitosamente');
      setShowModal(false);
      setEditingUser(null);
      resetUser();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error al actualizar usuario';
      showError(message);
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: userService.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showSuccess('Usuario eliminado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error al eliminar usuario';
      showError(message);
    }
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) => 
      userService.changeUserPassword(id, { password }),
    onSuccess: () => {
      showSuccess('Contraseña actualizada exitosamente');
      setShowPasswordModal(false);
      setSelectedUserId(null);
      resetPassword();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || error.message || 'Error al cambiar contraseña';
      showError(message);
    }
  });

  const handleCreateUser = () => {
    setEditingUser(null);
    resetUser();
    setShowModal(true);
  };

  const handleEditUser = (user: User) => {
    // Prevent non-superusers from editing superusers
    if (!canManageSuperusers() && isSuperuser(user)) {
      showError('No tienes permisos para editar un superusuario');
      return;
    }
    
    setEditingUser(user);
    resetUser({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role_id: user.role?.id,
      password: '' // Password is optional when editing
    });
    setShowModal(true);
  };

  const handleDeleteUser = (user: User) => {
    if (user.id === currentUser?.id) {
      showError('No puedes eliminar tu propio usuario');
      return;
    }

    // Prevent non-superusers from deleting superusers
    if (!canManageSuperusers() && isSuperuser(user)) {
      showError('No tienes permisos para eliminar un superusuario');
      return;
    }

    confirmAlert({
      title: 'Confirmar eliminación',
      message: `¿Estás seguro de que quieres eliminar al usuario ${userService.getUserFullName(user)}?`,
      buttons: [
        {
          label: 'Eliminar',
          onClick: () => deleteUserMutation.mutate(user.id)
        },
        {
          label: 'Cancelar'
        }
      ]
    });
  };

  const handleChangePassword = (user: User) => {
    // Prevent non-superusers from changing superuser passwords
    if (!canManageSuperusers() && isSuperuser(user)) {
      showError('No tienes permisos para cambiar la contraseña de un superusuario');
      return;
    }
    
    setSelectedUserId(user.id);
    resetPassword();
    setShowPasswordModal(true);
  };

  const onSubmitUser = async (data: CreateUserRequest) => {
    if (editingUser) {
      const updateData: UpdateUserRequest = {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        role_id: data.role_id
      };
      
      // Only include password if it was provided
      if (data.password) {
        (updateData as any).password = data.password;
      }
      
      await updateUserMutation.mutateAsync({ id: editingUser.id, data: updateData });
    } else {
      await createUserMutation.mutateAsync(data);
    }
  };

  const onSubmitPassword = async (data: { password: string }) => {
    if (selectedUserId) {
      await changePasswordMutation.mutateAsync({ id: selectedUserId, password: data.password });
    }
  };

  const toggleUserStatus = async (user: User) => {
    if (user.id === currentUser?.id) {
      showError('No puedes desactivar tu propio usuario');
      return;
    }

    // Prevent non-superusers from toggling superuser status
    if (!canManageSuperusers() && isSuperuser(user)) {
      showError('No tienes permisos para cambiar el estado de un superusuario');
      return;
    }

    await updateUserMutation.mutateAsync({
      id: user.id,
      data: { active: !user.active }
    });
  };

  if (isLoadingUsers || isLoadingRoles) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Administrador de Usuarios</h2>
          <p className="text-gray-600 text-sm mt-1">Gestiona los usuarios que pueden acceder al sistema</p>
        </div>
        <button
          onClick={handleCreateUser}
          className="btn-primary"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nuevo Usuario
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último acceso
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-sm">
                          {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                        </span>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {userService.getUserFullName(user)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.role?.name || 'Sin rol'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {userService.getUserStatusText(user)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {userService.formatLastLogin(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    {/* Only show action buttons if user is not a superuser OR current user is a superuser */}
                    {(canManageSuperusers() || !isSuperuser(user)) && (
                      <>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar usuario"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleChangePassword(user)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Cambiar contraseña"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 01 21 9z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => toggleUserStatus(user)}
                          className={user.active ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                          title={user.active ? "Desactivar usuario" : "Activar usuario"}
                          disabled={user.id === currentUser?.id}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {user.active ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            )}
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar usuario"
                          disabled={user.id === currentUser?.id}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                    {/* Show disabled message for superusers when current user is not superuser */}
                    {!canManageSuperusers() && isSuperuser(user) && (
                      <span className="text-gray-400 text-xs">Acciones restringidas</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No hay usuarios registrados</p>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </h3>
            
            <form onSubmit={handleSubmitUser(onSubmitUser)} className="space-y-4">
              <div>
                <label className="form-label">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...registerUser('first_name')}
                  className="form-input"
                  placeholder="Nombre"
                />
                {userErrors.first_name && (
                  <p className="form-error">{userErrors.first_name.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Apellido <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...registerUser('last_name')}
                  className="form-input"
                  placeholder="Apellido"
                />
                {userErrors.last_name && (
                  <p className="form-error">{userErrors.last_name.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...registerUser('email')}
                  className="form-input"
                  placeholder="usuario@empresa.com"
                />
                {userErrors.email && (
                  <p className="form-error">{userErrors.email.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Rol <span className="text-red-500">*</span>
                </label>
                <select
                  {...registerUser('role_id')}
                  className="form-input"
                  disabled={isLoadingRoles}
                >
                  <option value="">
                    {isLoadingRoles ? 'Cargando roles...' : 'Seleccionar rol'}
                  </option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name} - {role.description}
                    </option>
                  ))}
                </select>
                {userErrors.role_id && (
                  <p className="form-error">{userErrors.role_id.message}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  El rol determina los permisos y accesos del usuario en el sistema
                </p>
                
                {/* Roles Preview */}
                {!isLoadingRoles && roles.length > 0 && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <h5 className="text-xs font-medium text-gray-700 mb-2">Roles disponibles:</h5>
                    <div className="space-y-1">
                      {roles.map((role) => (
                        <div key={role.id} className="flex justify-between items-center text-xs">
                          <span className="font-medium text-gray-600">{role.name}</span>
                          <span className="text-gray-500 truncate ml-2" title={role.description}>
                            {role.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="form-label">
                  {editingUser ? 'Nueva Contraseña (opcional)' : 'Contraseña'} 
                  {!editingUser && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="password"
                  {...registerUser('password', { 
                    required: !editingUser ? 'La contraseña es requerida' : false 
                  })}
                  className="form-input"
                  placeholder="Mínimo 8 caracteres"
                />
                {userErrors.password && (
                  <p className="form-error">{userErrors.password.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                    resetUser();
                  }}
                  className="btn-outline"
                  disabled={isSubmittingUser}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmittingUser}
                >
                  {isSubmittingUser ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Cambiar Contraseña</h3>
            
            <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-4">
              <div>
                <label className="form-label">
                  Nueva Contraseña <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  {...registerPassword('password')}
                  className="form-input"
                  placeholder="Mínimo 8 caracteres"
                />
                {passwordErrors.password && (
                  <p className="form-error">{passwordErrors.password.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUserId(null);
                    resetPassword();
                  }}
                  className="btn-outline"
                  disabled={isSubmittingPassword}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmittingPassword}
                >
                  {isSubmittingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManager;